import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, User, LogOut, LayoutDashboard, Settings } from 'lucide-react';

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  
  // Check login status
  const [currentUser, setCurrentUser] = useState(null);
  const [userType, setUserType] = useState(null);
  
  useEffect(() => {
    // Check for customer login
    const customerUser = localStorage.getItem('customer_user');
    const customerToken = localStorage.getItem('customer_token');
    
    // Check for freelancer login
    const freelancerUser = localStorage.getItem('freelancer_user');
    const freelancerToken = localStorage.getItem('freelancer_token');
    
    if (customerUser && customerToken) {
      try {
        setCurrentUser(JSON.parse(customerUser));
        setUserType('customer');
      } catch {}
    } else if (freelancerUser && freelancerToken) {
      try {
        setCurrentUser(JSON.parse(freelancerUser));
        setUserType('freelancer');
      } catch {}
    } else {
      setCurrentUser(null);
      setUserType(null);
    }
  }, [location.pathname]); // Re-check on route change
  
  const handleLogout = () => {
    localStorage.removeItem('customer_user');
    localStorage.removeItem('customer_token');
    localStorage.removeItem('freelancer_user');
    localStorage.removeItem('freelancer_token');
    setCurrentUser(null);
    setUserType(null);
    setShowUserMenu(false);
    navigate('/');
  };
  
  const handleGoToDashboard = () => {
    setShowUserMenu(false);
    if (userType === 'customer') {
      navigate('/customer/dashboard');
    } else if (userType === 'freelancer') {
      navigate('/freelancer/dashboard');
    }
  };
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showUserMenu && !e.target.closest('[data-testid="user-menu-btn"]') && !e.target.closest('.user-dropdown')) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showUserMenu]);

  const navLinks = [
    { path: '/', label: 'Freelancer', isAnchor: true, anchor: '#map' },
    { path: '/services', label: 'Services' },
    { path: '/team', label: 'Über uns' },
  ];

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname === path;
  };

  const handleNavClick = (link) => {
    setMobileMenuOpen(false);
    
    if (link.isAnchor && location.pathname === '/') {
      document.querySelector(link.anchor)?.scrollIntoView({ behavior: 'smooth' });
    } else if (link.isAnchor) {
      navigate('/');
      setTimeout(() => {
        document.querySelector(link.anchor)?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      navigate(link.path);
    }
  };

  return (
    <>
      <nav 
        className="fixed top-0 left-0 right-0 px-6 lg:px-12 py-4" 
        style={{ backgroundColor: 'rgba(10, 10, 10, 0.95)', backdropFilter: 'blur(20px)', zIndex: 9998 }}
      >
        <div className="flex items-center justify-between">
          {/* Logo */}
          <button onClick={() => navigate('/')} className="flex items-center gap-0.5">
            <img 
              src="/revocut-logo.png" 
              alt="RevoCut Logo" 
              className="w-10 h-10 object-contain"
            />
            <span className="text-xl font-bold tracking-tight text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              REVOCUT
            </span>
          </button>

          {/* Desktop Nav Links */}
          <div className="hidden md:flex items-center gap-10">
            {navLinks.map((link) => (
              <button
                key={link.path}
                onClick={() => handleNavClick(link)}
                className="relative text-sm transition-colors py-1"
                style={{ 
                  color: isActive(link.path) ? '#ffffff' : '#9CA3AF',
                }}
              >
                {link.label}
                {isActive(link.path) && (
                  <span 
                    className="absolute bottom-0 left-0 right-0 h-0.5 rounded-full"
                    style={{ backgroundColor: '#5C63FF' }}
                  />
                )}
              </button>
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-4">
            {currentUser ? (
              <div className="relative">
                <button 
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all hover:bg-white/10 text-white border border-white/20"
                  data-testid="user-menu-btn"
                >
                  <div 
                    className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold"
                    style={{ backgroundColor: '#5C63FF' }}
                  >
                    {currentUser.name?.charAt(0).toUpperCase() || <User className="w-4 h-4" />}
                  </div>
                  <span className="max-w-24 truncate">{currentUser.name?.split(' ')[0]}</span>
                </button>
                
                {/* Dropdown Menu */}
                {showUserMenu && (
                  <div 
                    className="user-dropdown absolute right-0 top-full mt-2 w-48 rounded-xl border border-white/10 shadow-xl overflow-hidden"
                    style={{ backgroundColor: '#1a1a1a', zIndex: 9999 }}
                  >
                    <button
                      onClick={handleGoToDashboard}
                      className="w-full px-4 py-3 text-left text-sm hover:bg-white/5 flex items-center gap-2"
                    >
                      <LayoutDashboard className="w-4 h-4 text-purple-400" />
                      Dashboard
                    </button>
                    <button
                      onClick={handleLogout}
                      className="w-full px-4 py-3 text-left text-sm hover:bg-white/5 flex items-center gap-2 text-red-400"
                    >
                      <LogOut className="w-4 h-4" />
                      Abmelden
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button 
                onClick={() => navigate('/login')}
                className="px-4 py-2 rounded-full text-sm font-medium transition-all hover:bg-white/10 text-gray-300 border border-white/20"
              >
                Login
              </button>
            )}
            <button 
              onClick={() => {
                if (location.pathname === '/') {
                  document.querySelector('#map')?.scrollIntoView({ behavior: 'smooth' });
                } else {
                  navigate('/');
                }
              }}
              className="px-5 py-2.5 rounded-full text-sm font-semibold transition-all hover:scale-105 text-white"
              style={{ backgroundColor: '#5C63FF' }}
            >
              Jetzt starten
            </button>
          </div>

          {/* Mobile Burger Button */}
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 text-white"
            aria-label="Menü öffnen"
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 md:hidden"
          style={{ backgroundColor: 'rgba(10, 10, 10, 0.98)', zIndex: 9999 }}
        >
          <div className="flex flex-col items-center justify-center h-full gap-8 pt-20">
            {navLinks.map((link) => (
              <button
                key={link.path}
                onClick={() => handleNavClick(link)}
                className="text-2xl font-semibold transition-colors"
                style={{ 
                  color: isActive(link.path) ? '#5C63FF' : '#ffffff',
                  fontFamily: 'Montserrat, sans-serif'
                }}
              >
                {link.label}
              </button>
            ))}
            
            {/* Mobile CTA */}
            {currentUser ? (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <div 
                    className="w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold"
                    style={{ backgroundColor: '#5C63FF' }}
                  >
                    {currentUser.name?.charAt(0).toUpperCase() || 'U'}
                  </div>
                  <div>
                    <p className="text-white font-semibold">{currentUser.name}</p>
                    <p className="text-sm text-gray-400">{userType === 'customer' ? 'Kunde' : 'Freelancer'}</p>
                  </div>
                </div>
                <button 
                  onClick={() => {
                    setMobileMenuOpen(false);
                    handleGoToDashboard();
                  }}
                  className="px-8 py-3 rounded-full text-base font-medium transition-all text-white border border-white/20 flex items-center gap-2"
                >
                  <LayoutDashboard className="w-5 h-5" />
                  Dashboard
                </button>
                <button 
                  onClick={() => {
                    setMobileMenuOpen(false);
                    handleLogout();
                  }}
                  className="mt-2 px-8 py-3 rounded-full text-base font-medium transition-all text-red-400 border border-red-500/30"
                >
                  Abmelden
                </button>
              </>
            ) : (
              <>
                <button 
                  onClick={() => {
                    setMobileMenuOpen(false);
                    navigate('/login');
                  }}
                  className="px-8 py-3 rounded-full text-base font-medium transition-all text-white border border-white/20"
                >
                  Login
                </button>
                <button 
                  onClick={() => {
                    setMobileMenuOpen(false);
                    if (location.pathname === '/') {
                      document.querySelector('#map')?.scrollIntoView({ behavior: 'smooth' });
                    } else {
                      navigate('/');
                    }
                  }}
                  className="mt-2 px-8 py-4 rounded-full text-base font-semibold transition-all text-white"
                  style={{ backgroundColor: '#5C63FF' }}
                >
                  Jetzt starten
                </button>
              </>
            )}

            {/* Legal Links */}
            <div className="absolute bottom-8 flex flex-wrap justify-center gap-4 text-sm text-gray-500">
              <button onClick={() => { setMobileMenuOpen(false); navigate('/impressum'); }}>
                Impressum
              </button>
              <button onClick={() => { setMobileMenuOpen(false); navigate('/datenschutz'); }}>
                Datenschutz
              </button>
              <button onClick={() => { setMobileMenuOpen(false); navigate('/agb'); }}>
                AGB
              </button>
              <button onClick={() => { setMobileMenuOpen(false); navigate('/widerruf'); }}>
                Widerruf
              </button>
              <button onClick={() => { setMobileMenuOpen(false); navigate('/cookies'); }}>
                Cookies
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
